import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Modal,
  Stack,
  Group,
  Text,
  NumberInput,
  Button,
  Alert,
  Title,
} from "@mantine/core";
import {
  IconCurrencyDollar,
  IconAlertCircle,
  IconDeviceFloppy,
} from "@tabler/icons-react";

function EditProjectedRevenueModal({ isOpen, onClose, rep, onUpdate }) {
  const { getVerifiedJWT } = useAuth();
  const [projectedRevenue, setProjectedRevenue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prepopulate the modal when it opens or rep data changes
  useEffect(() => {
    if (isOpen && rep?.projected_revenue !== undefined) {
      setProjectedRevenue(rep.projected_revenue || 0);
    }
  }, [isOpen, rep?.projected_revenue]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const response = await fetch(
        getApiEndpoint(`/v1/rep/details/${rep.rep_id}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            projected_revenue: projectedRevenue || null,
          }),
        }
      );

      if (response.ok) {
        onUpdate(projectedRevenue);
        onClose();
        setError(null);
      } else {
        const data = await response.json();
        setError(data.message || "Failed to update projected revenue");
      }
    } catch (err) {
      setError("Failed to update projected revenue");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setProjectedRevenue(rep?.projected_revenue || 0);
    setError(null);
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleCancel}
      title={
        <Group gap="xs">
          <IconCurrencyDollar size={20} />
          <Title order={3} fw={600}>
            Edit Projected Revenue
          </Title>
        </Group>
      }
      size="md"
      padding="xl"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <Stack gap="lg">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Enter the estimated revenue for this representative. This will be
            used for commission calculations and reporting.
          </Text>

          <NumberInput
            label="Projected Revenue"
            placeholder="Enter amount"
            value={projectedRevenue}
            onChange={setProjectedRevenue}
            leftSection={<IconCurrencyDollar size={16} />}
            thousandSeparator=","
            decimalScale={0}
            min={0}
            max={99999999}
            size="md"
            disabled={loading}
            styles={{
              input: {
                fontSize: "16px",
              },
            }}
          />
        </Stack>

        <Group justify="flex-end" gap="sm" pt="md">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
            size="md"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={loading}
            leftSection={loading ? undefined : <IconDeviceFloppy size={16} />}
            size="md"
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditProjectedRevenueModal;
